import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { createDriverByBoss } from "../../../services/authService";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material/styles";

const RegisterDriver = () => {
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    hireDate: "", // YYYY-MM-DD
    username: "",
    password: "",
    revenuePercentage: 40,
  });
  const [dialog, setDialog] = useState({ open: false, title: "", message: "" });
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "revenuePercentage" ? Number(value) : value,
    }));
  };

  const parseErrorMessage = (rawMessage) => {
    if (rawMessage?.includes("E11000") && rawMessage?.includes("username")) {
      return "Ya existe un usuario con ese nombre. Prueba con otro.";
    }
    return rawMessage || "No se pudo registrar el conductor.";
  };

  const handleRegister = async () => {
    setSuccess("");
    try {
      await createDriverByBoss({
        name: formData.name,
        lastName: formData.lastName,
        hireDate: formData.hireDate,
        username: formData.username,
        password: formData.password,
        revenuePercentage: formData.revenuePercentage,
      });
      setSuccess("Conductor registrado exitosamente.");
      setTimeout(() => navigate("/drivers"), 1800);
    } catch (err) {
      setDialog({
        open: true,
        title: "No se pudo registrar",
        message: parseErrorMessage(err.message),
      });
    }
  };

  const closeDialog = () => setDialog({ open: false, title: "", message: "" });

  const isFormComplete = useMemo(
    () =>
      Boolean(
        formData.name &&
          formData.lastName &&
          formData.hireDate &&
          formData.username &&
          formData.password &&
          formData.revenuePercentage !== ""
      ),
    [formData]
  );

  const cardBg = isDark ? "#1e2a40" : "#fdfcfc"; // alineado con modal
  const inputBg = isDark ? colors.primary[500] : "#ececec";
  const inputText = isDark ? colors.grey[100] : "#000000";
  const labelColor = isDark ? "#ffffff" : "#000000";
  const borderBase = isDark ? "#2b3b55" : "#d1d5db";
  const borderFocus = colors.blueAccent[400];

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputBg,
      "& fieldset": {
        borderColor: borderBase,
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: borderBase,
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: borderFocus,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: labelColor,
      "&.Mui-focused": {
        color: colors.grey[100], // que el label flotante siga blanco al hacer focus
      },
    },
    "& .MuiInputBase-input": {
      color: inputText,
    },
  };

  const primaryBtnStyles = {
    bgcolor: colors.blueAccent[500],
    color: "#ffffff",
    fontWeight: 600,
    "&:hover": { bgcolor: colors.blueAccent[400] },
  };

  const secondaryBtnStyles = {
    borderColor: borderBase,
    color: inputText,
    "&:hover": { borderColor: borderFocus, color: inputText },
  };

  return (
    <Box display="flex" justifyContent="center" mt={4}>
      <Paper
        elevation={4}
        sx={{
          width: "100%",
          maxWidth: 820,
          p: 4,
          backgroundColor: cardBg,
          border: `1px solid ${borderBase}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 12px 28px rgba(0,0,0,0.35)"
              : "0 12px 28px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Registrar Conductor
            </Typography>
            <Typography variant="subtitle1" color={colors.grey[300]}>
              Alta rápida para conductores asignados a tu flota.
            </Typography>
          </Box>
          <Button variant="outlined" sx={secondaryBtnStyles} onClick={() => navigate("/drivers")}>
            Volver
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              fullWidth
              required
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Apellidos"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              margin="normal"
              fullWidth
              required
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha de alta"
              type="date"
              name="hireDate"
              value={formData.hireDate}
              onChange={handleChange}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Porcentaje recaudación (%)"
              type="number"
              name="revenuePercentage"
              value={formData.revenuePercentage}
              onChange={handleChange}
              margin="normal"
              fullWidth
              required
              inputProps={{ min: 0, max: 100, step: 1 }}
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre de usuario"
              name="username"
              value={formData.username}
              onChange={handleChange}
              margin="normal"
              fullWidth
              required
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contraseña"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              fullWidth
              required
              sx={inputStyles}
            />
          </Grid>
        </Grid>

        <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
          <Button variant="outlined" sx={secondaryBtnStyles} onClick={() => navigate("/drivers")}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRegister}
            disabled={!isFormComplete}
            sx={primaryBtnStyles}
          >
            Registrar
          </Button>
        </Box>
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{dialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} autoFocus>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisterDriver;
